<template>
  <div class="project-progress-container">
    <!-- 标题区域 -->
    <div class="title-section">
      <h3 class="title">项目进度</h3>
    </div>

    <!-- Loading 状态 -->
    <div v-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <p class="loading-text">加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-section">
      <div class="error-icon">⚠️</div>
      <p class="error-text">{{ error }}</p>
      <button class="retry-btn" @click="fetchProgressData">重试</button>
    </div>

    <!-- 空状态 -->
    <div v-else-if="progressNodes.length === 0" class="empty-section">
      <div class="empty-icon">📊</div>
      <p class="empty-text">暂无进度节点</p>
    </div>

    <!-- 进度条展示 -->
    <div v-else class="progress-section">
      <!-- 项目管理进度 -->
      <div v-if="pmisNodes.length > 0" class="progress-group">


        <!-- 项目管理进度时间线 -->
        <div class="progress-timeline" ref="pmisTimeline">
          <!-- 进度线 -->
          <div class="progress-line">
            <div
              class="progress-line-filled pmis-progress"
              :style="{ width: getPmisCompletionRate() + '%' }"
            ></div>
          </div>

          <!-- 进度节点 -->
          <div class="progress-nodes">
            <div
              v-for="(node, index) in pmisNodes"
              :key="'pmis-' + index"
              class="progress-node"
              :class="{
                'completed': node.finished,
                'current': !node.finished && isPreviousCompleted(pmisNodes, index),
                'pending': !node.finished && !isPreviousCompleted(pmisNodes, index)
              }"
              :style="{ left: getNodePosition(pmisNodes, index) + '%' }"
              @mouseenter="showTooltip(node, $event)"
              @mouseleave="hideTooltip"
            >
              <div class="node-dot pmis-node">
                <span v-if="node.finished" class="node-icon">✓</span>
                <span v-else-if="!node.finished && isPreviousCompleted(pmisNodes, index)" class="node-icon">●</span>
                <span v-else class="node-icon">○</span>
              </div>

              <!-- 节点标签 -->
              <div
                v-if="shouldShowLabel(pmisNodes, index)"
                class="node-label"
                :class="{ 'label-up': index % 2 === 0, 'label-down': index % 2 === 1 }"
              >
                <div class="node-name">{{ node.name }}</div>
                <div v-if="node.finished && node.finished_at" class="node-date">
                  {{ formatDate(node.finished_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 监理进度 -->
      <div v-if="jlisNodes.length > 0" class="progress-group">
        <div class="progress-group-title">
          <span class="group-title-text">监理进度</span>
          <span class="completion-rate">{{ getJlisCompletionRate() }}%</span>
        </div>

        <!-- 监理进度时间线 -->
        <div class="progress-timeline" ref="jlisTimeline">
          <!-- 进度线 -->
          <div class="progress-line">
            <div
              class="progress-line-filled jlis-progress"
              :style="{ width: getJlisCompletionRate() + '%' }"
            ></div>
          </div>

          <!-- 进度节点 -->
          <div class="progress-nodes">
            <div
              v-for="(node, index) in jlisNodes"
              :key="'jlis-' + index"
              class="progress-node"
              :class="{
                'completed': node.finished,
                'current': !node.finished && isPreviousCompleted(jlisNodes, index),
                'pending': !node.finished && !isPreviousCompleted(jlisNodes, index)
              }"
              :style="{ left: getNodePosition(jlisNodes, index) + '%' }"
              @mouseenter="showTooltip(node, $event)"
              @mouseleave="hideTooltip"
            >
              <div class="node-dot jlis-node">
                <span v-if="node.finished" class="node-icon">✓</span>
                <span v-else-if="!node.finished && isPreviousCompleted(jlisNodes, index)" class="node-icon">●</span>
                <span v-else class="node-icon">○</span>
              </div>

              <!-- 节点标签 -->
              <div
                v-if="shouldShowLabel(jlisNodes, index)"
                class="node-label"
                :class="{ 'label-up': index % 2 === 0, 'label-down': index % 2 === 1 }"
              >
                <div class="node-name">{{ node.name }}</div>
                <div v-if="node.finished && node.finished_at" class="node-date">
                  {{ formatDate(node.finished_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 无数据提示 -->
      <div v-if="pmisNodes.length === 0 && jlisNodes.length === 0" class="no-progress-data">
        <div class="empty-icon">📊</div>
        <p class="empty-text">暂无进度数据</p>
      </div>
    </div>

    <!-- 工具提示 -->
    <div
      v-if="tooltip.show"
      class="tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      <div class="tooltip-title">{{ tooltip.node.name }}</div>
      <div class="tooltip-platform">{{ getPlatformName(tooltip.node.platform) }}</div>
      <div class="tooltip-status" :class="{ 'completed': tooltip.node.finished }">
        {{ tooltip.node.finished ? '已完成' : '待完成' }}
      </div>
      <div v-if="tooltip.node.finished && tooltip.node.finished_at" class="tooltip-date">
        完成时间：{{ formatDate(tooltip.node.finished_at) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    env: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      progressNodes: [],
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        node: null,
      },
    };
  },
  computed: {
    pmisNodes() {
      return this.progressNodes.filter((node) => node.platform === 'pmis').sort((a, b) => a.sortCode - b.sortCode);
    },

    jlisNodes() {
      return this.progressNodes.filter((node) => node.platform === 'jlis').sort((a, b) => a.sortCode - b.sortCode);
    },
  },
  mounted() {
    this.fetchProgressData();
  },
  watch: {
    'env.project.zzl_id': {
      handler() {
        if (this.env?.project?.zzl_id) {
          this.fetchProgressData();
        }
      },
    },
  },
  methods: {
    async fetchProgressData() {
      if (!this.env?.project?.zzl_id) {
        this.error = '项目ID不存在';
        return;
      }

      const pid = this.env.project.zzl_id;
      this.loading = true;
      this.error = null;

      try {
        const response = await this.$api.get(`logined_zzl/project_progress_nodes?pid=${pid}`);

        if (response.data && response.data.data) {
          this.progressNodes = Array.isArray(response.data.data) ?
            response.data.data.sort((a, b) => a.sortCode - b.sortCode) :
            [response.data.data];
        } else {
          this.progressNodes = [];
        }
      } catch (error) {
        console.error('获取项目进度失败:', error);
        this.error = '获取项目进度失败，请稍后重试';
        this.progressNodes = [];
      } finally {
        this.loading = false;
      }
    },

    isPreviousCompleted(nodes, index) {
      if (index === 0) return true;
      return nodes.slice(0, index).every((node) => node.finished);
    },

    getNodePosition(nodes, index) {
      if (nodes.length <= 1) return 50;

      // 当节点较多时，调整边距避免标签溢出
      const leftMargin = 8;
      const rightMargin = 8;
      const availableWidth = 100 - leftMargin - rightMargin;

      if (nodes.length === 2) {
        return leftMargin + (index * availableWidth);
      } else {
        return leftMargin + (index / (nodes.length - 1)) * availableWidth;
      }
    },

    getPmisCompletionRate() {
      if (this.pmisNodes.length === 0) return 0;
      const completed = this.pmisNodes.filter((node) => node.finished).length;
      return Math.round((completed / this.pmisNodes.length) * 100);
    },

    getJlisCompletionRate() {
      if (this.jlisNodes.length === 0) return 0;
      const completed = this.jlisNodes.filter((node) => node.finished).length;
      return Math.round((completed / this.jlisNodes.length) * 100);
    },

    getPlatformName(platform) {
      return platform === 'pmis' ? '项目管理' : platform === 'jlis' ? '监理' : '未知';
    },

    formatDate(dateString) {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('zh-CN', {
          month: 'short',
          day: 'numeric',
        });
      } catch (error) {
        return dateString;
      }
    },

    showTooltip(node, event) {
      this.tooltip = {
        show: true,
        x: event.clientX,
        y: event.clientY - 10,
        node: node,
      };
    },

    hideTooltip() {
      this.tooltip.show = false;
    },

    shouldShowLabel(nodes, index) {
      // 节点较少时全部显示
      if (nodes.length <= 6) return true;

      const node = nodes[index];

      // 总是显示已完成的节点
      if (node.finished) return true;

      // 总是显示当前节点（第一个未完成且前面都完成的节点）
      if (!node.finished && this.isPreviousCompleted(nodes, index)) return true;

      // 显示最后2个节点
      if (index >= nodes.length - 2) return true;

      // 显示第一个节点
      if (index === 0) return true;

      return false;
    },
  },
};
</script>

<style scoped>
.project-progress-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
}

.title-section {
  margin-bottom: 16px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: var(--text-primary, #333);
}

/* Loading 状态 */
.loading-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border, #f0f0f0);
  border-top: 3px solid var(--primary, #007bff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
}

/* 错误状态 */
.error-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.error-icon {
  font-size: 32px;
}

.error-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
  text-align: center;
}

.retry-btn {
  background: var(--primary, #007bff);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: var(--primary-dark, #0056b3);
}

/* 空状态 */
.empty-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.empty-icon {
  font-size: 32px;
  opacity: 0.6;
}

.empty-text {
  color: var(--text-secondary, #666);
  font-size: 14px;
  margin: 0;
}

/* 进度展示区域 */
.progress-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
}

.progress-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #00000005;
  border-radius: 8px;
  padding: 0px;
}

.progress-group-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  height:100%;
  background: white;
  border-radius: 6px;
  border-left: 4px solid var(--primary, #007bff);
}

.group-title-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #333);
}

.completion-rate {
  font-size: 13px;
  font-weight: bold;
  color: var(--primary, #007bff);
}

.no-progress-data {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* 进度时间线 */
.progress-timeline {
  position: relative;
  flex: 1;
  min-height: 160px;
  margin: 20px 0;
  padding: 0 40px;
}

.progress-line {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 40px;
  right: 40px;
  height: 4px;
  background: var(--bg-secondary, #e8e8e8);
  border-radius: 2px;
}

.progress-line-filled {
  height: 100%;
  border-radius: 2px;
  transition: width 0.5s ease;
}

.progress-line-filled.pmis-progress {
  background: linear-gradient(90deg, var(--success, #28a745), var(--primary, #007bff));
}

.progress-line-filled.jlis-progress {
  background: linear-gradient(90deg, #ff6b6b, #ff8e53);
}

.progress-nodes {
  position: relative;
  height: 100%;
}

.progress-node {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.node-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;
  border: 3px solid;
  background: white;
  position: relative;
  z-index: 2;
}

.progress-node.completed .node-dot {
  border-color: var(--success, #28a745);
  color: var(--success, #28a745);
  background: var(--success, #28a745);
  color: white;
}

.progress-node.current .node-dot {
  border-color: var(--primary, #007bff);
  color: var(--primary, #007bff);
  animation: pulse 2s infinite;
}

.progress-node.pending .node-dot {
  border-color: var(--border, #e0e0e0);
  color: var(--text-secondary, #999);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.node-label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  font-size: 10px;
  width: 70px;
  z-index: 1;
}

.node-label.label-up {
  bottom: 35px;
}

.node-label.label-down {
  top: 35px;
}

.node-name {
  font-weight: 500;
  color: var(--text-primary, #333);
  margin-bottom: 2px;
  word-wrap: break-word;
  line-height: 1.1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-date {
  font-size: 9px;
  color: var(--text-secondary, #666);
  line-height: 1;
}

/* 项目管理节点样式 */
.progress-node.completed .node-dot.pmis-node {
  border-color: var(--success, #28a745);
  background: var(--success, #28a745);
  color: white;
}

.progress-node.current .node-dot.pmis-node {
  border-color: var(--primary, #007bff);
  color: var(--primary, #007bff);
}

/* 监理节点样式 */
.progress-node.completed .node-dot.jlis-node {
  border-color: #ff6b6b;
  background: #ff6b6b;
  color: white;
}

.progress-node.current .node-dot.jlis-node {
  border-color: #ff8e53;
  color: #ff8e53;
}

/* 工具提示 */
.tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  z-index: 1000;
  pointer-events: none;
  max-width: 200px;
}

.tooltip-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.tooltip-platform {
  font-size: 11px;
  color: var(--primary, #87CEEB);
  margin-bottom: 2px;
}

.tooltip-status {
  margin-bottom: 2px;
}

.tooltip-status.completed {
  color: var(--success, #90EE90);
}

.tooltip-date {
  font-size: 11px;
  opacity: 0.8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .project-progress-container {
    padding: 12px;
  }

  .progress-timeline {
    min-height: 140px;
    padding: 0 20px;
  }

  .progress-line {
    left: 20px;
    right: 20px;
  }

  .node-label {
    font-size: 9px;
    width: 60px;
  }

  .node-label.label-up {
    bottom: 28px;
  }

  .node-label.label-down {
    top: 28px;
  }

  .node-dot {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .node-name {
    -webkit-line-clamp: 1;
  }

  .node-date {
    font-size: 8px;
  }
}
</style>
